import {SelectionChange, SelectionModel} from '@angular/cdk/collections';
import {TemplateRef} from '@angular/core';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {DeleteValidationResponseObj} from '../../../../generated/generated-core.model';
import {CopyPasteConfig} from '../../../shared/interfaces/copy-paste-config';
import {PreloaderConfig} from '../../mx-preloader/services/mx-preloader.service';
import {GridActionService} from './mx-grid-actions.service';
import {MxGridButtonsConfig} from './mx-grid-button.model';
import {GridConfigStorageService} from './mx-grid-config-storage.service';
import {GridGrandTotalsService} from './mx-grid-grand-totals.service';
import {GridCollectionService} from './mx-grid-collection.service';
import {GridColumnService} from './mx-grid-column.service';
import {GridOrderService} from './mx-grid-order.service';
import {GridPropsService} from './mx-grid-props.service';
import {first, take} from 'rxjs/operators';
import {SelectionType} from './selection-type.model';
import {GridColumn} from './grid-column';

export interface MxGridReload {
    doReload(): void;
}

export type MxGridDisableRowFn = (row: any, selection?: SelectionModel<any>, collection?: any) => false | string;

export type MxGridSelectionChangedFn = (selectionChange: SelectionChange<any>, selectionModel: SelectionModel<any>) => void;

export class MxGridRemoveConfig {

    confirm?: boolean = true;

    doRemove?: (ids: Array<number | string>) => Observable<Array<number | string>>;

    validateRemove?: (ids: Array<number | string>, shouldValidate: boolean) => Observable<DeleteValidationResponseObj>;

    shouldValidate?: boolean = false;

    recordsName?: string = 'record(s)';

    confirmMessage?: (selection: any[]) => string | boolean
        = (selection: any[]) =>
        `Are you sure you want to remove ${selection.length} selected ${this.recordsName}?`
}

export type OnSetupFn = (service: MxGridService) => void;

export type EmptyGridParams = () => {
    image: string,
    text: string
};

export interface MxGridExtractId<T> {
    extractId (row: T): number | string;
}

export interface MxGridWithThumbnail {
    getThumbnailClass (element: any): string;

    getThumbnailUrl (element: any): string;

    getThumbnailColumnName(): string;
}

export interface MxGridGrouping {
    key: string;
    displayValue: string;
    infoKey?: string;
}

export interface MxGridHandleOrder<T> {
    getOrder (row: T): number;

    setOrder (order: number, row: T): T;
}

export class MxGridExtractIdDefault implements MxGridExtractId<any> {
    extractId(row: any): number {
        return row.id;
    }
}

/** Current inputs of the grid */
export class MxGridProps {
    rowQueryParams?: Array<{[k: string]: any}> = [];
    allowReorder?: boolean = false;
    allowGroupReorder?: boolean = false;
    allowSelectAll?: boolean = true;
    dateFormat?: string;
    selectionMode?: SelectionType = 'multi';
    paginationLimit?: number = 25;
    pageIndex?: number = 0;
    noPagination?: boolean = false;
    sortActive?: string = '';
    sortDirection?: '' | 'asc' | 'desc' = '';
    isGoToItemDetailsAllowed?: boolean = true;
    showColumns?: boolean = true;
    asyncSaveOrder?: boolean = true;
    isHeaderHidden?: boolean = false;
    showItemDetailsInDialog?: boolean = false;
    readonly?: boolean = false;
    routeLinkRelative?: boolean = false;
    detailWithParams?: boolean = false;
    hideEmptyColumn?: boolean = false;
    hideUndefinedColumn?: boolean = false;
    handleGridErrors?: boolean = false;
    saveGridState?: boolean = true;
    gridId?: string = '';
    useQueryParam?: boolean = true;
    showButtons?: boolean = false;
}

export class MxGridHandleOrderDefault implements MxGridHandleOrder<any> {

    private propName: string;

    constructor(propName: string = 'order') {
        this.propName = propName;
    }

    getOrder(row: any): number {
        return row[this.propName];
    }

    setOrder(order: number, row: any): any {
        row[this.propName] = order;
        return row;
    }
}

export class MxGridService {
    actions: GridActionService;
    collection: GridCollectionService;
    columns: GridColumnService;
    grandTotals: GridGrandTotalsService;
    order: GridOrderService;
    props: GridPropsService;
    reRender: () => void;
    storage: GridConfigStorageService;
}

export class MxGridCustomizerEvent {
    private _actions: Array<MxGridButtonsConfig> = [];
    private _buttonsTemplate: TemplateRef<any>;
    private _orderCustomizer: MxGridHandleOrder<any>;
    private _orderUrl: string;
    private _gridReload: MxGridReload;
    private _extractId: MxGridExtractId<any>;
    private _restUrl: string;
    private _itemDetailsLink: string;
    private _permission: string;
    private _thumbnailData: MxGridWithThumbnail;
    private _disableRow: MxGridDisableRowFn;
    private _selectionChangedFn: MxGridSelectionChangedFn;
    private _copyPasteConfig: CopyPasteConfig;
    private _preloaderConfig: PreloaderConfig;
    private _rowErrorsSource$: Subject<Map<number, Map<string, string>>>;
    private _boldRow: (row: any) => boolean;

    /** Load data on start */
    private _loadRowsOnStart: boolean = false;
    private _rowsSource$: Observable<Array<any>>;
    private _columnsSource$: Observable<Array<GridColumn>>;
    private _orderChanged$: Subject<void> = new Subject();

    private _emptyGridParams: EmptyGridParams;

    private _grandTotals: ReadonlyMap<string, number | string>;

    get grandTotals(): ReadonlyMap<string, number | string> {
        return this._grandTotals;
    }

    private _setUp$$: Subject<MxGridService> = new Subject<MxGridService>();
    private _removeConfig: Array<MxGridRemoveConfig> = [];
    private _props: Array<MxGridProps> = [];
    private _assignedItemsKeys: Array<string | number> = [];
    private _groupByColumn: (row: any) => {key: string, displayValue: string};

    public onSetUp(onSetUp: OnSetupFn): Subscription {
        return this._setUp$$.asObservable().pipe(first())
            .subscribe((serv) => onSetUp(serv));
    }

    /** Add columns to the grid */
    customizeColumns(columns: Array<GridColumn>): this {
        this._columnsSource$ = of(columns);
        return this;
    }

    /** Set thumbnail data (to use it, component should implement MxGridWithThumbnail and then, override its methods) */
    customizeThumbnailData(gridWithThumbnail: MxGridWithThumbnail): this {
        this._thumbnailData = gridWithThumbnail;
        return this;
    }

    /** Function to group rows by some key */
    customizeGroupByColumn(groupByColumn: (row: any) => MxGridGrouping): this {
        this._groupByColumn = groupByColumn;
        return this;
    }

    /** Set initial data to the grid */
    customizeRowsSource(rowsSource: Array<any> | Observable<Array<any>>): this {
        if (rowsSource instanceof Array) {
            this._rowsSource$ = of(rowsSource);
        } else {
            this._rowsSource$ = rowsSource;
        }
        return this;
    }

    /** Used to store total values for columns in a table */
    customizeGrandTotals(grandTotals: ReadonlyMap<string, number | string>): this {
        this._grandTotals = grandTotals;
        return this;
    }

    /** Load data on start (For now, it use in every grid, but
      * this needed until we will set this to true by default) */
    customizeLoadRowsOnStart(value: boolean): this {
        this._loadRowsOnStart = value;
        return this;
    }

    /** Customize grid reload function */
    customizeGridReload(value: MxGridReload): this {
        this._gridReload = value;
        return this;
    }

    /** Functions to handle rows order in the grid */
    customizeOrder(value: MxGridHandleOrder<any>, updateUrl?: string): this {
        if (value) {
            this._orderCustomizer = value;
        }
        if (updateUrl) {
            this._orderUrl = updateUrl;
        }
        return this;
    }

    /** Add custom buttons to the grid and override its default buttons */
    customizeActions(actions: MxGridButtonsConfig): this {
        this._actions.push(actions);
        return this;
    }

    /** Add possibility to add button from ng-template */
    customizeButtonsTemplate(buttonsTemplate: TemplateRef<any>): this {
        this._buttonsTemplate = buttonsTemplate;
        return this;
    }

    /** Customize general props of the grid: pagination, sorting, selection mode, reordering etc. (see MxGridProps) */
    customizeProps(props: MxGridProps): this {
        if (props.noPagination === true && props.paginationLimit) {
            console.trace('There is no reason to set noPagination=true and customize paginationLimit');
        }

        if (props.sortActive && !props.sortDirection) {
            console.trace('There is no reason to set sortActive and do not set sortDirection');
        }

        if (!props.sortActive && props.sortDirection) {
            console.trace('There is no reason to do not set sortActive and set sortDirection');
        }

        if (props.sortActive && this.columnsSource$) {
            this.columnsSource$.pipe(take(1)).subscribe(columns => {
                if (columns.findIndex(c => c.prop === props.sortActive) === -1) {
                    console.trace(`There is no column with sortActive name ${props.sortActive}`);
                }
            });
        }

        this._props.push(props);
        return this;
    }

    /** Determine which text and image to show when the grid is empty */
    customizeEmptyGridParams(params: EmptyGridParams): this {
        this._emptyGridParams = params;
        return this;
    }

    /** Customize preloader name and type(such as: RoundSpinner, LinearSpinner or None) */
    customizePreloaderConfig(config: PreloaderConfig): this {
        this._preloaderConfig = config;
        return this;
    }

    /** Function to set a row as bold */
    customizeBoldRow(boldRow: (row: any) => boolean): this {
        this._boldRow = boldRow;
        return this;
    }

    get boldRow(): (row: any) => boolean {
        return this._boldRow;
    }

    get emptyGridParams(): EmptyGridParams {
        return this._emptyGridParams;
    }

    get preloaderConfig(): PreloaderConfig {
        return this._preloaderConfig;
    }

    get gridProps(): MxGridProps {
        return this._props.reduce((acc, curr) => Object.assign(acc, curr), new MxGridProps());
    }

    get assignedItemsKeys(): Array<string | number> {
        return this._assignedItemsKeys;
    }

    set assignedItemsKeys(assignedItemsKeys: Array<string | number>) {
        this._assignedItemsKeys = assignedItemsKeys;
    }

    get removeConfig(): MxGridRemoveConfig {
        return this._removeConfig.reduce((acc, curr) => Object.assign(acc, curr), new MxGridRemoveConfig());
    }

    /** Customize remove configuration: validation, doRemove function, confirmation and confirmMessage */
    customizeRemoveConfig(value: MxGridRemoveConfig): this {
        this._removeConfig.push(value);
        return this;
    }

    get actions(): Array<MxGridButtonsConfig> {
        return this._actions;
    }

    get buttonsTemplate(): TemplateRef<any> {
        return this._buttonsTemplate;
    }

    get orderUrl(): string {
        return this._orderUrl || this._restUrl;
    }

    get orderCustomizer(): MxGridHandleOrder<any> {
        return this._orderCustomizer || new MxGridHandleOrderDefault();
    }

    get gridReload(): MxGridReload {
        return this._gridReload || null;
    }

    get extractId(): MxGridExtractId<any> {
        return this._extractId || new MxGridExtractIdDefault();
    }

    get thumbnailData(): MxGridWithThumbnail {
        return this._thumbnailData;
    }

    get groupByColumn(): (row: any) => MxGridGrouping {
        return this._groupByColumn;
    }

    get restUrl(): string {
        return this._restUrl;
    }

    get itemDetailsLink(): string {
        return this._itemDetailsLink;
    }

    get permission(): string {
        return this._permission;
    }

    get rowsSource$(): Observable<Array<any>> {
        return this._rowsSource$;
    }

    get columnsSource$(): Observable<Array<GridColumn>> {
        return this._columnsSource$;
    }

    get orderChanged$(): Subject<void> {
        return this._orderChanged$;
    }

    get loadRowsOnStart(): boolean {
        return this._loadRowsOnStart;
    }

    /** Set error observable source */
    customizeErrorSource(source: Subject<Map<number, Map<string, string>>>): void {
        this._rowErrorsSource$ = source;
    }

    get rowErrorsSource(): Subject<Map<number, Map<string, string>>> {
        return this._rowErrorsSource$;
    }

    /** Define which identifier is extracted by extractId method */
    customizeExtractId(value: MxGridExtractId<any>): this {
        this._extractId = value;
        return this;

    }

    /** Set REST API URL (it is equal to restLink in routing.module) */
    customizeRestUrl(restLink: string): this {
        if (restLink) {
            this._restUrl = restLink;
        }
        return this;
    }

    /** Use when we need several grids in one component with different restUrls
      * For example: Product -> Source variables
      * (contains two grids: Product variables and Cookie templates with different restUrls),
      * so we need to specify itemDetailsLink for each grid */
    customizeItemDetailsLink(routeLink: string): this {
        if (routeLink) {
            this._itemDetailsLink = routeLink;
        }
        return this;
    }

    // Called only by the grid (TODO hide the method)
    notifySetUp(services: MxGridService): void {
        this._setUp$$.next(services);
    }

    /** Define a function to disable a row */
    customizeDisableRow(action: MxGridDisableRowFn): this {
        this._disableRow = action;
        return this;
    }

    get disableRow(): MxGridDisableRowFn {
        return this._disableRow;
    }

    /** Customize behavior when the selection changes */
    customizeSelectionChangedFn(action: MxGridSelectionChangedFn): this {
        this._selectionChangedFn = action;
        return this;
    }

    get selectionChangedFn(): MxGridSelectionChangedFn {
        return this._selectionChangedFn;
    }

    /** Define classType and copy-paste config to use for copy-paste operations */
    customizeCopyPaste(config: CopyPasteConfig): this {
        this._copyPasteConfig = config;
        return this;
    }

    get copyPasteConfig(): CopyPasteConfig {
        return this._copyPasteConfig;
    }

    /** Define permission required to access the grid */
    customizePermission(permission: string): this {
        if (permission) {
            this._permission = permission;
        }
        return this;
    }
}

